import { Link } from 'react-router-dom';
import { FolderOpen, MessageCircle, Phone } from 'lucide-react';

import type { ConversaDetalhe } from '@/api/secretario';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { ate, classesDoTom, formatarTelefone, haQuanto, linkTelefone, linkWhatsApp, nomeExibido, quando, situacaoDe, type Tom } from './util';

/** Alvo de toque de 44 px no celular; o tamanho normal a partir do tablet. */
const ALVO = 'min-h-11 sm:min-h-9';

function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function porExtenso(itens: string[]): string {
  return itens.length <= 1 ? itens.join('') : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

function contar(n: number, singular: string, plural: string): string | null {
  return n > 0 ? `${n} ${n === 1 ? singular : plural}` : null;
}

function perguntasSemResposta(dossie: ConversaDetalhe): number {
  return dossie.questionario.filter((p) => !(p.resposta ?? '').trim() && !p.removida && p.obrigatoria && !p.condicao).length;
}

/** Nome, situação, área, advogado, quando; telefone com ligação e WhatsApp. */
export function Cabecalho({ dossie }: { dossie: ConversaDetalhe }) {
  const c = dossie.conversa;
  const situacao = situacaoDe(c);
  const representacao = dossie.representacao as { menor?: { nome?: string }; representante?: { nome?: string; relacao?: string } } | null;
  const momento =
    c.estado === 'CONCLUSAO'
      ? `concluído ${quando(c.concluido_em)}`
      : c.estado === 'ESCALONAMENTO_HUMANO'
        ? `escalado ${quando(dossie.escalonamento?.escalado_em ?? c.ultima_atividade_em)}`
        : `iniciado ${quando(c.created_at)}`;
  const resumo = c.estado === 'CONCLUSAO' ? resumoDoConcluido(dossie) : '';

  return (
    <header className="space-y-2">
      <h1 className="text-xl font-bold leading-tight sm:text-2xl">{nomeExibido(c)}</h1>
      {representacao?.menor?.nome && (
        <p className="text-sm text-muted-foreground">
          Parte: {representacao.menor.nome} (menor)
          {representacao.representante?.nome
            ? ` · fala pela parte: ${representacao.representante.nome}${representacao.representante.relacao ? `, ${representacao.representante.relacao}` : ''}`
            : ''}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Badge variant="outline" className={classesDoTom(situacao.tom)}>
          {situacao.rotulo}
        </Badge>
        {c.area_nome && <span className="font-medium text-foreground">{c.area_nome}</span>}
        {c.advogado_nome && <span>· {c.advogado_nome}</span>}
        <span>· {momento}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <a
          href={linkTelefone(c.contato_telefone)}
          className={`inline-flex ${ALVO} items-center gap-2 rounded-md border bg-card px-3 text-sm hover:bg-accent/40`}
        >
          <Phone className="h-4 w-4" aria-hidden="true" />
          {formatarTelefone(c.contato_telefone)}
        </a>
        <a
          href={linkWhatsApp(c.contato_telefone)}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex ${ALVO} items-center gap-2 rounded-md border bg-card px-3 text-sm hover:bg-accent/40`}
        >
          <MessageCircle className="h-4 w-4" aria-hidden="true" />
          WhatsApp
        </a>
        {dossie.cliente && (
          <Button asChild variant="outline" size="sm" className={ALVO}>
            <Link to={`/app/clientes/${dossie.cliente.id}`}>
              <FolderOpen className="mr-2 h-4 w-4" aria-hidden="true" />
              Abrir pasta do cliente
            </Link>
          </Button>
        )}
      </div>
      {resumo && <p className="text-xs text-muted-foreground">{resumo}</p>}
    </header>
  );
}

/**
 * Uma linha que diz o que o atendimento produziu, com o substantivo de cada
 * número e sem contagem zerada: "Cadastro criado neste atendimento · contrato de
 * honorários e procuração entregues · 2 documentos conferidos".
 */
function resumoDoConcluido(dossie: ConversaDetalhe): string {
  const entregues = dossie.instrumentos
    .filter((i) => i.status === 'ASSINADO')
    .map((i) => (i.titulo ?? 'documento de contratação').toLowerCase());
  const partes = [
    dossie.cliente ? (dossie.cliente.criado_neste_atendimento ? 'cadastro criado neste atendimento' : 'cliente já cadastrado') : null,
    entregues.length > 0 ? `${porExtenso(entregues)} ${entregues.length === 1 ? 'entregue' : 'entregues'}` : null,
    contar(dossie.documentos.filter((d) => d.conferido).length, 'documento conferido', 'documentos conferidos'),
    contar(dossie.documentos.filter((d) => d.status === 'DISPENSADO').length, 'documento dispensado', 'documentos dispensados'),
    contar(perguntasSemResposta(dossie), 'pergunta obrigatória sem resposta', 'perguntas obrigatórias sem resposta'),
  ].filter((parte): parte is string => parte !== null);
  return partes.length > 0 ? capitalizar(partes.join(' · ')) : '';
}

interface Item {
  tom: Tom;
  texto: string;
  detalhe?: string;
  /** O fato que diz o estado: é nele que as ações do estado ficam. */
  principal?: boolean;
  link?: { para: string; rotulo: string };
}

/**
 * Só fatos que pedem algo do advogado, cada um com a cor do que exige. As ações
 * do estado ficam no fato que as pede: ao lado dele em tela larga, embaixo dele
 * no tablet, e na barra fixa no celular.
 */
export function FaixaDeAtencao({ dossie, acoes }: { dossie: ConversaDetalhe; acoes?: React.ReactNode }) {
  const itens: Item[] = [];
  const seg = dossie.seguimento;
  const esc = dossie.escalonamento;

  if (seg) {
    if (seg.desfecho === 'PROCESSO') {
      const por = seg.desfecho_por_nome ? ` (${seg.desfecho_por_nome})` : '';
      itens.push(
        seg.processo
          ? {
              tom: 'ok',
              texto: `Virou o processo ${seg.processo.numero} ${quando(seg.desfecho_em)}${por}.`,
              detalhe: seg.processo.no_escopo
                ? 'O Secretário passa a responder "como está meu processo?" com o andamento dele.'
                : 'O processo saiu do seu escopo (arquivado ou de outro advogado); o vínculo continua registrado.',
            }
          : {
              tom: 'ok',
              texto: `Virou processo ${quando(seg.desfecho_em)}${por}.`,
              detalhe: 'O processo vinculado não está mais disponível no sistema.',
            },
      );
    } else if (seg.desfecho === 'ENCERRADA') {
      itens.push({
        tom: 'neutral',
        texto: `Encerrado ${quando(seg.desfecho_em)}${seg.desfecho_por_nome ? ` por ${seg.desfecho_por_nome}` : ''}: ${seg.desfecho_motivo ?? 'sem motivo registrado'}.`,
        detalhe: 'O Secretário diz ao cliente que o escritório já deu o retorno e não há processo em curso.',
      });
    } else if (seg.retorno_em) {
      itens.push({
        principal: true,
        tom: 'neutral',
        texto: `Retorno registrado ${quando(seg.retorno_em)}${seg.retorno_por_nome ? ` por ${seg.retorno_por_nome}` : ''}. Em preparação ${haQuanto(seg.retorno_em)}, sem processo vinculado.`,
        detalhe: seg.retorno_observacao ? `Observação: ${seg.retorno_observacao}` : undefined,
      });
    } else if (seg.concluido_em) {
      itens.push({
        principal: true,
        tom: seg.prazo_vencido ? 'bad' : 'warn',
        texto: seg.prazo_vencido
          ? `Retorno prometido ao cliente ${ate(seg.prazo_ate)} (${seg.prazo_retorno_horas} h): prazo vencido.`
          : `Retorno prometido ao cliente ${ate(seg.prazo_ate)} (em até ${seg.prazo_retorno_horas} h).`,
        detalhe: seg.atraso_avisado_em
          ? `O cliente voltou a escrever depois do prazo ${quando(seg.atraso_avisado_em)} e foi avisado de que o atraso chegou a você.`
          : undefined,
      });
    }
  }

  if (esc) {
    itens.push({
      principal: true,
      tom: esc.assumido_em ? 'ok' : 'bad',
      texto: `${esc.rotulo}${esc.assumido_em ? ` · assumido ${quando(esc.assumido_em)}${esc.assumido_por_nome ? ` por ${esc.assumido_por_nome}` : ''}` : ' · ninguém assumiu'}.`,
      detalhe: esc.resumo ? `${esc.origem === 'ia' ? 'Resumo escrito pelo Secretário (IA)' : 'Detalhe'}: "${esc.resumo}"` : undefined,
    });
    if (esc.atendimento_origem_id) {
      itens.push({
        tom: 'neutral',
        texto: 'Sobre um atendimento concluído deste número.',
        link: { para: `/app/secretario-ia/atendimentos/${esc.atendimento_origem_id}`, rotulo: 'Abrir o atendimento de origem' },
      });
    }
  }

  const semResposta = perguntasSemResposta(dossie);
  if (semResposta > 0) {
    itens.push({
      tom: 'bad',
      texto: semResposta === 1 ? '1 pergunta obrigatória ficou sem resposta.' : `${semResposta} perguntas obrigatórias ficaram sem resposta.`,
    });
  }
  dossie.documentos
    .filter((d) => d.status === 'DISPENSADO' || d.status === 'REJEITADO' || d.status === 'INFECTADO')
    .forEach((d) =>
      itens.push({ tom: d.status === 'DISPENSADO' ? 'neutral' : 'warn', texto: `${d.item_nome}: ${d.rotulo_status}${d.motivo ? ` — "${d.motivo}"` : ''}.` }),
    );

  const comAcoes = acoes ? itens.findIndex((item) => item.principal) : -1;
  if (itens.length === 0 && !acoes) return null;
  return (
    <div className="space-y-1.5">
      {itens.length > 0 && (
        <ul className="space-y-1.5" aria-label="O que pede atenção">
          {itens.map((item, i) => (
            <li
              key={i}
              className={`flex flex-col gap-2 rounded-md border px-3 py-2 text-sm lg:flex-row lg:items-center lg:justify-between ${classesDoTom(item.tom)}`}
            >
              <span className="flex min-w-0 items-start gap-2.5">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-current" aria-hidden="true" />
                <span>
                  <span className="text-foreground">{item.texto}</span>
                  {item.detalhe && <span className="mt-0.5 block text-xs text-muted-foreground">{item.detalhe}</span>}
                </span>
              </span>
              {item.link && (
                <Button asChild variant="link" className="h-auto min-h-11 justify-start px-0 sm:min-h-0 lg:shrink-0">
                  <Link to={item.link.para}>{item.link.rotulo}</Link>
                </Button>
              )}
              {i === comAcoes && <div className="hidden sm:block lg:shrink-0">{acoes}</div>}
            </li>
          ))}
        </ul>
      )}
      {acoes && comAcoes === -1 && <div className="hidden sm:block">{acoes}</div>}
    </div>
  );
}

type AcaoDoEstado = 'assumir' | 'retorno' | 'vincular' | 'encerrar';

/**
 * As ações de cada estado, na ordem em que aparecem; a primeira é a primária.
 * A página usa a mesma regra para decidir se há barra fixa no celular.
 */
export function acoesDoEstado(dossie: ConversaDetalhe): AcaoDoEstado[] {
  const c = dossie.conversa;
  const seg = dossie.seguimento;
  const esc = dossie.escalonamento;
  if (c.estado === 'CONCLUSAO' && seg?.concluido_em && !seg.desfecho) {
    return seg.retorno_em ? ['vincular', 'encerrar'] : ['retorno'];
  }
  if (c.estado === 'ESCALONAMENTO_HUMANO' && esc) {
    const acoes: AcaoDoEstado[] = [];
    if (!esc.assumido_em) acoes.push('assumir');
    if (esc.categoria === 'RETORNO_ATRASO' && !esc.retorno_em) acoes.push('retorno');
    return acoes;
  }
  return [];
}

const ROTULO_DA_ACAO: Record<AcaoDoEstado, string> = {
  assumir: 'Assumir',
  retorno: 'Retorno feito',
  vincular: 'Vincular processo',
  encerrar: 'Encerrar',
};

export interface AcoesProps {
  dossie: ConversaDetalhe;
  ocupado: boolean;
  onRetorno: () => void;
  onAssumir: () => void;
  onVincular: () => void;
  onEncerrar: () => void;
}

/** Uma primária por estado; as secundárias ao lado. */
export function Acoes({ dossie, ocupado, onRetorno, onAssumir, onVincular, onEncerrar }: AcoesProps) {
  const acoes = acoesDoEstado(dossie);
  if (acoes.length === 0) return null;
  const aoClicar: Record<AcaoDoEstado, () => void> = { assumir: onAssumir, retorno: onRetorno, vincular: onVincular, encerrar: onEncerrar };
  return (
    <div className="flex flex-wrap gap-2">
      {acoes.map((acao, i) => (
        <Button
          key={acao}
          type="button"
          variant={i === 0 ? 'default' : 'outline'}
          onClick={aoClicar[acao]}
          disabled={ocupado}
          className="min-h-11 flex-1 sm:min-h-9 sm:flex-none"
        >
          {ROTULO_DA_ACAO[acao]}
        </Button>
      ))}
    </div>
  );
}
